import { useEffect, useState } from "react";
import Table from "components/Table/Table";
import { useLocation, useNavigate } from "react-router-dom";
import type { CellClickArgs } from "react-data-grid";
import { useGetClaimantsQuery } from "services/claimant-api";
import type { ClaimantType } from "types/DataTypes";
import ClaimantPageContainer from "./ClaimantPageContainer";
import { commonColumnProps, OFFSET, PAGE_SIZE } from "constants/TableConstants";
import { useQuery } from "utils/hooks";
import { GLOBAL_SEARCH_PARAM } from "utils/useUrlStore";

import { setIsLoading } from "reducers/main";
import { useDispatch } from "utils/redux";

type ColumnDefinition = {
  key: keyof ClaimantType;
  name: string;
};

const CLAIMANT_COLUMNS: ColumnDefinition[] = [
  { key: "first_name", name: "First Name", ...commonColumnProps },
  { key: "middle_name", name: "Middle Name", ...commonColumnProps },
  { key: "last_name", name: "Last Name", ...commonColumnProps },
  { key: "ssn", name: "SSN", ...commonColumnProps },
  { key: "birth_date", name: "Date of Birth", ...commonColumnProps },
  { key: "gender", name: "Gender", ...commonColumnProps },
  { key: "address", name: "Address", ...commonColumnProps },
  { key: "state", name: "State", ...commonColumnProps },
  { key: "zip_code", name: "Zip Code", ...commonColumnProps },
];

const ClaimantTable = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const query = useQuery();
  const [page, setPage] = useState(1);

  const { data = { results: [], count: 0 }, isLoading } = useGetClaimantsQuery(
    {
      searchTerm: query.get(GLOBAL_SEARCH_PARAM),
      limit: PAGE_SIZE,
      offset: OFFSET(page),
    },
    { skip: false },
  );

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  const handleCellClick = (args: CellClickArgs<ClaimantType, unknown>) => {
    navigate(`${location.pathname}/${args.row.id}/overview`);
  };

  return (
    <ClaimantPageContainer>
      <Table
        className="claimants-table"
        columns={CLAIMANT_COLUMNS}
        rows={data.results}
        rowKeyGetter={(row: ClaimantType) => row.id}
        onCellClick={handleCellClick}
        fullHeight
        pagination={{
          pageSize: PAGE_SIZE,
          pageIndex: page,
          rowCount: data.count,
          onPageChange: setPage,
          isLoading,
        }}
      />
    </ClaimantPageContainer>
  );
};

export default ClaimantTable;
